// components/LandingPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom,
  Stack,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Church,
  People,
  Event,
  AttachMoney,
  VolunteerActivism,
  Groups,
  Favorite,
  School,
  Healing,
  FoodBank,
  Email,
  Phone,
  LocationOn,
  AccessTime,
  ArrowForward,
  PlayCircle,
  CheckCircle,
  TrendingUp,
  Security,
  Support,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  Close,
  CalendarToday,
  Notifications,
  PersonAdd,
  Send,
  Star,
  StarBorder,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Inventory,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '150px',
    background: 'linear-gradient(to top, #f5f7fa, transparent)',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s, box-shadow 0.3s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  '&::before': {
    content: '"❝"',
    position: 'absolute',
    top: -10,
    left: 10,
    fontSize: '4rem',
    color: alpha(theme.palette.primary.main, 0.1),
    fontFamily: 'Georgia, serif',
  },
}));

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: People,
      title: 'Member Management',
      description: 'Easily manage member profiles, attendance, and communication with our comprehensive member management system.',
      color: '#1976d2',
    },
    {
      icon: Event,
      title: 'Event Planning',
      description: 'Schedule and manage church events, track registrations, and coordinate volunteers seamlessly.',
      color: '#2e7d32',
    },
    {
      icon: AttachMoney,
      title: 'Finance & Donations',
      description: 'Track donations, manage budgets, and generate financial reports with transparency and ease.',
      color: '#ed6c02',
    },
    {
      icon: Groups,
      title: 'Small Groups',
      description: 'Organize and manage small groups, Bible studies, and community gatherings effectively.',
      color: '#9c27b0',
    },
    {
      icon: VolunteerActivism,
      title: 'Volunteer Management',
      description: 'Recruit, schedule, and coordinate volunteers for various church ministries and events.',
      color: '#d32f2f',
    },
    {
      icon: Inventory,
      title: 'Inventory Management',
      description: 'Track church resources, supplies, and equipment with our inventory management system.',
      color: '#00695c',
    },
  ];

  const stats = [
    { number: '1,284', label: 'Members', icon: People },
    { number: '42', label: 'Ministries', icon: Groups },
    { number: '156', label: 'Volunteers', icon: VolunteerActivism },
    { number: '850', label: 'Weekly Attendance', icon: Event },
  ];

  const testimonials = [
    {
      name: 'Pastor John Smith',
      role: 'Senior Pastor',
      content: 'This platform has transformed how we manage our church operations. It\'s intuitive, comprehensive, and has helped us grow our community significantly.',
      avatar: 'JS',
    },
    {
      name: 'Sarah Johnson',
      role: 'Youth Director',
      content: 'The youth ministry features are incredible! We\'ve seen a 40% increase in participation since we started using this system.',
      avatar: 'SJ',
    },
    {
      name: 'Michael Chen',
      role: 'Finance Committee',
      content: 'Financial transparency has never been easier. The donation tracking and reporting features are exactly what we needed.',
      avatar: 'MC',
    },
  ];

  const ministries = [
    { name: 'Youth Ministry', icon: School, members: 85, color: '#1976d2' },
    { name: 'Worship Team', icon: Healing, members: 45, color: '#2e7d32' },
    { name: 'Community Outreach', icon: Favorite, members: 120, color: '#ed6c02' },
    { name: 'Food Bank', icon: FoodBank, members: 30, color: '#9c27b0' },
  ];

  const navItems = [
    { label: 'Home', anchor: 'home' },
    { label: 'Features', anchor: 'features' },
    { label: 'Ministries', anchor: 'ministries' },
    { label: 'About', anchor: 'about' },
    { label: 'Contact', anchor: 'contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      if (mobileOpen) setMobileOpen(false);
    }
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2 }}>
        <Church sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ my: 1, color: 'primary.main' }}>
          Grace Church
        </Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => scrollToSection(item.anchor)}
            sx={{
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                textAlign: 'center',
                '& .MuiTypography-root': {
                  fontWeight: activeSection === item.anchor ? 600 : 400,
                  color: activeSection === item.anchor ? 'primary.main' : 'text.primary',
                },
              }}
            />
          </ListItem>
        ))}
        <ListItem>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => window.location.href = '/login'}
          >
            Login
          </Button>
        </ListItem>
        <ListItem>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => window.location.href = '/register'}
          >
            Get Started
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box>
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          bgcolor: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s',
          color: scrolled ? 'text.primary' : 'white',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Church sx={{ mr: 1, fontSize: 32 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Grace Church
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => scrollToSection(item.anchor)}
                  sx={{
                    color: scrolled ? 'text.primary' : 'white',
                    fontWeight: activeSection === item.anchor ? 600 : 400,
                    borderBottom: activeSection === item.anchor ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    '&:hover': {
                      bgcolor: 'transparent',
                      borderBottom: '2px solid',
                      borderColor: scrolled ? 'primary.main' : 'white',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="outlined"
                sx={{
                  color: scrolled ? 'primary.main' : 'white',
                  borderColor: scrolled ? 'primary.main' : 'white',
                  '&:hover': {
                    borderColor: scrolled ? 'primary.dark' : 'white',
                    bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.05) : 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => window.location.href = '/login'}
              >
                Login
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => window.location.href = '/register'}
              >
                Get Started
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Hero Section */}
      <HeroSection id="home">
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    label="Welcome to Grace Church"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      mb: 3,
                      fontSize: '0.9rem',
                      '& .MuiChip-label': { px: 2 },
                    }}
                    icon={<Church sx={{ color: 'white' }} />}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'white',
                      mb: 2,
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                    }}
                  >
                    Manage Your Church
                    <Box component="span" sx={{ color: 'secondary.main', display: 'block' }}>
                      With Excellence
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      mb: 4,
                      maxWidth: '500px',
                      fontWeight: 300,
                      lineHeight: 1.8,
                    }}
                  >
                    Empower your ministry with our comprehensive church management platform.
                    Streamline operations, engage members, and grow your community.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => window.location.href = '/register'}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      startIcon={<PlayCircle />}
                    >
                      Watch Demo
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
                    <Box>
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                        1,284+
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Members
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                        42
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Ministries
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                        156
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Volunteers
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Slide direction="left" in timeout={1000}>
                <Box sx={{ position: 'relative' }}>
                  <Paper
                    elevation={20}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 4,
                      maxWidth: 450,
                      margin: '0 auto',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <Church />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Quick Stats
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Today's Overview
                        </Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f7fa' }}>
                          <Typography variant="h5" color="primary.main">
                            847
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Attendance Today
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f7fa' }}>
                          <Typography variant="h5" color="secondary.main">
                            $3,200
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Donations Today
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Upcoming Events
                            </Typography>
                            <Chip label="3 Today" size="small" color="primary" />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label="9:00 AM Worship" size="small" />
                            <Chip label="11:00 AM Worship" size="small" />
                            <Chip label="6:00 PM Prayer" size="small" />
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        🔒 Secure & Private
                      </Typography>
                    </Box>
                  </Paper>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 100,
                      height: 100,
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                      borderRadius: '50%',
                      zIndex: -1,
                    }}
                  />
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Box id="features" sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="Features"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography variant="h2" gutterBottom>
              Everything You Need
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Comprehensive tools to manage every aspect of your church ministry effectively
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={500 + index * 100}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(feature.color, 0.1),
                          color: feature.color,
                          width: 56,
                          height: 56,
                          mb: 2,
                        }}
                      >
                        <feature.icon />
                      </Avatar>
                      <Typography variant="h6" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box sx={{ py: 8, bgcolor: 'primary.main' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <StatCard elevation={0}>
                  <stat.icon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </StatCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Ministries Section */}
      <Box id="ministries" sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="Ministries"
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: 'secondary.main',
                mb: 2,
              }}
            />
            <Typography variant="h2" gutterBottom>
              Our Ministries
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Connect and grow through our diverse ministries
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {ministries.map((ministry, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(ministry.color, 0.1),
                      color: ministry.color,
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <ministry.icon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {ministry.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ministry.members} Members
                  </Typography>
                  <Button
                    size="small"
                    color="primary"
                    sx={{ mt: 2 }}
                    endIcon={<ArrowForward />}
                  >
                    Learn More
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="Testimonials"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography variant="h2" gutterBottom>
              What Our Members Say
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <TestimonialCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{testimonial.content}"
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', color: 'secondary.main' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} sx={{ fontSize: 20 }} />
                      ))}
                    </Box>
                  </CardContent>
                </TestimonialCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, bgcolor: 'secondary.main' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography variant="h3" gutterBottom>
              Ready to Grow Your Ministry?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
              Join thousands of churches using our platform to manage and grow their community
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'secondary.main',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                }}
                endIcon={<ArrowForward />}
                onClick={() => window.location.href = '/register'}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box id="contact" sx={{ py: 6, bgcolor: 'primary.dark', color: 'white' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Church sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={700}>
                  Grace Church
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Empowering churches with technology to fulfill their mission and grow their community.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Facebook />
                </IconButton>
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Twitter />
                </IconButton>
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Instagram />
                </IconButton>
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <YouTube />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['About Us', 'Services', 'Events', 'Ministries'].map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      cursor: 'pointer',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 16, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    123 Church Street, City
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 16, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    (555) 123-4567
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 16, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    info@gracechurch.com
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Newsletter
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Subscribe to get updates and news
              </Typography>
              <Box sx={{ display: 'flex' }}>
                <TextField
                  placeholder="Your Email"
                  size="small"
                  sx={{
                    flex: 1,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: '50px 0 0 50px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px 0 0 50px',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputBase-input': { color: 'white', py: 1 },
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{
                    borderRadius: '0 50px 50px 0',
                    px: 3,
                  }}
                  endIcon={<Send />}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="caption" sx={{ opacity: 0.6, textAlign: 'center', display: 'block' }}>
            © 2026 Grace Church. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;